// The ranged attack, staged (issue 1527; docs/MRCS/shadowrun-3.md §2.4, §3.2.4, §3.2.5, §3.6, §5.5; the book p. 109–114):
// the attacker's skill and pool dice against a target number built from the range band, the situations, the visibility
// and the attacker's wounds; then the defender's dodge (pool dice against the dodge TN and the defender's wounds) and
// resistance (a sheet rating and pool dice against the weapon's Power less the armour the attack meets, never the
// wounds); then the damage code staged by the net successes — attack less dodge and resistance, every `stage_per`
// moving the level one step, at most the top level, below the lowest no damage — and its boxes down the chain of the
// code's kind of damage (§3.2.5, step 12's chain).
//
// Melee (issue 1528; §5.6; the book p. 120–123) is the same record with no band and no dodge: both sides throw their
// melee skill and pool dice at the melee TN, each with its own situations, visibility (at half in melee, but Full
// darkness), wound and the reach; more successes hits — the defender too — and a tie goes to the attacker; the winner's
// net successes stage the winner's weapon up, past the top adding Power (`beyond_top: "power"`); the one hit resists
// with its rating and the pool dice left against that Power less its Impact armour, every `stage_per` one step down.
//
// The record is an attack's (`AttackFacts.staged`, rules/attack.ts), so privacy, the log, the damage setting and the
// projection are the attack's own. Nothing here throws a die on its own: the server hands the generator in, a test a
// scripted list. Each step is worked out from the record alone, so a replay and every viewer read the same.
import { diceSum, parseDiceTerms } from './magicDice.js';
import { MAX_POOL, parsePool, poolDisaster, poolFaces, poolResult, poolText, testDice, throwPool } from '../dice/pool.js';
import { parseCodes, codesData, parseTestDice, FIRING_LIMITS } from './system.js';
import { testDiceData } from './test.js';
export const STAGED_REQUEST_LIMITS = { name: 80, rating: MAX_POOL, power: 100, situations: 24, count: 10, modifiers: 8, modifier: 50, label: 40, reach: 10 };
/** The word the recoil's TN modifier is shown with (issue 1529): "TN 7 (3 + 2 recoil + …)". */
export const STAGED_RECOIL = 'recoil';
/** The rounds a mode throws: its own, or the attacker's choice within its range; null when the choice is not one of them. */
export function fireRounds(m, asked) {
    if (typeof m.rounds === 'number')
        return asked === undefined || asked === m.rounds ? m.rounds : null;
    const n = asked ?? m.rounds.least;
    return Number.isInteger(n) && n >= m.rounds.least && n <= m.rounds.most ? n : null;
}
/**
 * A firing worked out (issue 1529; p. 110, 114–115): the recoil is every round of the phase so far and this firing's,
 * times the mode's recoil, less its free rounds and the compensation, at least none, times the heavy weapon's
 * multiplier; the Power rises the mode's `power` a round and the level one step every `levelPer` rounds (at most the
 * top, Deadly); the dodge's TN one point every `dodgePer` rounds.
 */
export function fireOf(m, rounds, prior, compensation, heavy, base, levels, top) {
    const points = rounds * m.recoil;
    const recoil = Math.max(0, prior + points - m.free - compensation) * heavy;
    const raise = m.levelPer ? Math.min(Math.floor(rounds / m.levelPer), Math.max(0, top - levels)) : 0;
    return {
        mode: m.key, label: m.label, rounds, prior, points, free: m.free, compensation, heavy, recoil: Math.min(STAGED_REQUEST_LIMITS.modifier, recoil),
        base: { ...base }, power: rounds * m.power, levels: raise, dodge: m.dodgePer ? Math.floor(rounds / m.dodgePer) : 0,
    };
}
/** The over-damage to stamp on an attack: the system's rule, where the campaign has it on. Absent, none. */
export const overOf = (on, r) => (on && r.overDamage ? { label: r.overDamage.label, times: r.overDamage.times } : null);
/** The armour kind a staged attack's resistance meets: melee's for a melee attack or a blast, else ranged's. */
export const stagedArmourKind = (f) => (f.melee || f.armourAs === 'melee' ? 'melee' : 'ranged');
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
function parseTerms(v, most) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > most)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, STAGED_REQUEST_LIMITS.label) || !isInt(x.value, -STAGED_REQUEST_LIMITS.modifier, STAGED_REQUEST_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
/** A weapon off the wire: exact shape or null. A Strength-based code may add nothing to the Strength (`(STR)M`). */
export function parseStagedWeapon(w) {
    if (!isObj(w) || !only(w, ['name', 'power', 'level', 'damageKind', 'wielder']) || !isText(w.name, STAGED_REQUEST_LIMITS.name) || !isKey(w.level))
        return null;
    if (w.wielder !== undefined && w.wielder !== true)
        return null;
    if (!isInt(w.power, w.wielder ? -STAGED_REQUEST_LIMITS.reach : 1, STAGED_REQUEST_LIMITS.power))
        return null;
    if (w.damageKind !== undefined && !isKey(w.damageKind))
        return null;
    return { name: w.name.trim(), power: w.power, level: w.level, ...(typeof w.damageKind === 'string' ? { damageKind: w.damageKind } : {}), ...(w.wielder ? { wielder: true } : {}) };
}
/** A skill off the wire: its name, rating (1 to 60) and content address. */
function parseSkill(s) {
    if (!isObj(s) || !only(s, ['name', 'rating', 'ref']) || !isText(s.name, STAGED_REQUEST_LIMITS.name) || !isInt(s.rating, 1, STAGED_REQUEST_LIMITS.rating))
        return null;
    if (s.ref !== undefined && (typeof s.ref !== 'string' || !ADDRESS_RE.test(s.ref)))
        return null;
    return { name: s.name.trim(), rating: s.rating, ...(typeof s.ref === 'string' ? { ref: s.ref } : {}) };
}
function parseSituationList(v) {
    const sits = v === undefined ? [] : v;
    if (!Array.isArray(sits) || sits.length > STAGED_REQUEST_LIMITS.situations)
        return null;
    const situations = [];
    for (const x of sits) {
        if (!isObj(x) || !only(x, ['key', 'count']) || !isKey(x.key) || (x.count !== undefined && !isInt(x.count, 1, STAGED_REQUEST_LIMITS.count)))
            return null;
        situations.push({ key: x.key, ...(x.count !== undefined ? { count: x.count } : {}) });
    }
    return new Set(situations.map((x) => x.key)).size === situations.length ? situations : null;
}
const isReach = (v) => isInt(v, 0, STAGED_REQUEST_LIMITS.reach);
function parseReach(v) {
    if (!isObj(v) || !only(v, ['attacker', 'defender', 'way']) || !isReach(v.attacker) || !isReach(v.defender) || (v.way !== 'own' && v.way !== 'other'))
        return null;
    return { attacker: v.attacker, defender: v.defender, way: v.way };
}
function parseMeleeRequest(v) {
    if (!isObj(v) || !only(v, ['reach', 'defender']))
        return null;
    const reach = v.reach === undefined ? undefined : parseReach(v.reach);
    const d = v.defender;
    if (reach === null || !isObj(d) || !only(d, ['situations', 'vision', 'modifiers']))
        return null;
    const situations = parseSituationList(d.situations);
    const modifiers = parseTerms(d.modifiers, STAGED_REQUEST_LIMITS.modifiers);
    if (!situations || !modifiers || (d.vision !== undefined && !isKey(d.vision)))
        return null;
    return { ...(reach ? { reach } : {}), defender: { situations, ...(typeof d.vision === 'string' ? { vision: d.vision } : {}), modifiers } };
}
export function parseMeleeAnswer(v) {
    if (!isObj(v) || !only(v, ['skill', 'weapon']))
        return null;
    const skill = parseSkill(v.skill);
    const weapon = parseStagedWeapon(v.weapon);
    return skill && weapon ? { skill, weapon } : null;
}
/** A staged attack request from the wire: exact shape or null. Untrusted input, always. */
export function parseStagedRequest(v) {
    if (!isObj(v) || v.kind !== 'attack.staged' || !only(v, ['kind', 'attacker', 'target', 'weapon', 'skill', 'band', 'melee', 'situations', 'visibility', 'modifiers', 'pool', 'private', 'fire', 'astral', 'sheetRow']))
        return null;
    // eslint-disable-next-line no-control-regex
    if (v.sheetRow !== undefined && (typeof v.sheetRow !== 'string' || !/^[^\u0000-\u001f\u007f]{1,64}$/.test(v.sheetRow)))
        return null;
    // Astral combat (issue 1531) is melee.
    if (v.astral !== undefined && (v.astral !== true || v.melee === undefined))
        return null;
    if (!UUID_RE.test(String(v.attacker)) || !UUID_RE.test(String(v.target)) || v.attacker === v.target)
        return null;
    const weapon = parseStagedWeapon(v.weapon);
    const skill = parseSkill(v.skill);
    if (!weapon || !skill)
        return null;
    // A band (ranged) or a melee part, never both.
    if ((v.band === undefined) === (v.melee === undefined))
        return null;
    if (v.band !== undefined && !isKey(v.band))
        return null;
    const melee = v.melee === undefined ? undefined : parseMeleeRequest(v.melee);
    if (melee === null)
        return null;
    const situations = parseSituationList(v.situations);
    if (!situations)
        return null;
    let visibility;
    if (v.visibility !== undefined) {
        const x = v.visibility;
        if (!isObj(x) || !only(x, ['condition', 'vision']) || !isKey(x.condition) || !isKey(x.vision))
            return null;
        visibility = { condition: x.condition, vision: x.vision };
    }
    const modifiers = parseTerms(v.modifiers, STAGED_REQUEST_LIMITS.modifiers);
    if (!modifiers)
        return null;
    let pool;
    if (v.pool !== undefined) {
        const p = v.pool;
        if (!isObj(p) || !only(p, ['key', 'dice']) || !isKey(p.key) || !isInt(p.dice, 1, STAGED_REQUEST_LIMITS.rating) || skill.rating + p.dice > MAX_POOL)
            return null;
        pool = { key: p.key, dice: p.dice };
    }
    const priv = v.private === undefined ? false : v.private;
    if (typeof priv !== 'boolean')
        return null;
    // A firing mode (issue 1529): a ranged attack's.
    let fire;
    if (v.fire !== undefined) {
        const f = v.fire;
        if (melee || !isObj(f) || !only(f, ['mode', 'rounds', 'compensation', 'heavy']) || typeof f.mode !== 'string' || !/^[A-Z]{1,4}$/.test(f.mode))
            return null;
        if ((f.rounds !== undefined && !isInt(f.rounds, 1, FIRING_LIMITS.rounds)) || !isInt(f.compensation, 0, FIRING_LIMITS.compensation) || (f.heavy !== undefined && f.heavy !== true))
            return null;
        fire = { mode: f.mode, ...(f.rounds !== undefined ? { rounds: f.rounds } : {}), compensation: f.compensation, ...(f.heavy ? { heavy: true } : {}) };
    }
    return {
        kind: 'attack.staged', attacker: String(v.attacker), target: String(v.target), weapon, skill,
        ...(typeof v.band === 'string' ? { band: v.band } : {}), ...(melee ? { melee } : {}),
        situations, ...(visibility ? { visibility } : {}), modifiers, ...(pool ? { pool } : {}), private: priv, ...(fire ? { fire } : {}),
        ...(v.astral === true ? { astral: true } : {}), ...(typeof v.sheetRow === 'string' ? { sheetRow: v.sheetRow } : {}),
    };
}
// ── The TN and the throws ──────────────────────────────────────────────────
/** What a system without a staged attack is told. */
export const NO_STAGED_RULES = "This table's game system has no attack made against a target number, so the table cannot make one.";
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
const minTarget = (d) => d.success?.minTarget ?? 1;
const tnOf = (d, target, terms) => Math.max(minTarget(d), target + sum(terms.map((t) => t.value)));
const bounded = (n) => Math.max(-STAGED_REQUEST_LIMITS.modifier, Math.min(STAGED_REQUEST_LIMITS.modifier, n));
/** The word a wound's TN modifier is shown with: "TN 5 (4 + 1 wounded)". */
export const STAGED_WOUND = 'wounded';
/** The word the reach's modifier is shown with (issue 1528): "TN 3 (4 − 1 reach)". */
export const STAGED_REACH = 'reach';
/**
 * The reach's terms (p. 121; §5.6): the difference between the two reaches, for the longer side −1 a point on its own
 * TN, or +1 a point on the other's, as the form chose. Equal reaches: none.
 */
export function reachTerms(reach) {
    const none = { attacker: [], defender: [] };
    if (!reach || reach.attacker === reach.defender)
        return none;
    const longer = reach.attacker > reach.defender ? 'attacker' : 'defender';
    const d = Math.abs(reach.attacker - reach.defender);
    const shorter = longer === 'attacker' ? 'defender' : 'attacker';
    return reach.way === 'own' ? { ...none, [longer]: [{ label: STAGED_REACH, value: -d }] } : { ...none, [shorter]: [{ label: STAGED_REACH, value: d }] };
}
/** A visibility condition's modifier in melee (issue 1528): half, rounded down, unless the condition (or table) says whole. */
const meleeVisibility = (rules, c, n) => ((c.melee ?? rules.visibility?.melee ?? 'full') === 'half' ? Math.floor(n / 2) : n);
/**
 * The attack's TN terms (§5.5), from the request by the system's lists, or the refusal in words: each situation ticked
 * (its modifier, times how many when it counts, at most its `most`), the visibility condition for the attacker's vision,
 * the typed ones, the reach's (melee), then the attacker's wound. A situation for the other kind of attack is refused.
 */
export function attackTerms(req, rules, wound, kind = 'ranged', reach = [], recoil = 0) {
    const terms = [];
    for (const x of req.situations) {
        const s = rules.situations.find((y) => y.key === x.key);
        if (!s || (s.only !== null && s.only !== kind))
            return `That situation is not one of this game system's for a ${kind} attack.`;
        if (x.count !== undefined && s.per !== 'count')
            return `${s.label} is not counted.`;
        if (x.count !== undefined && s.most !== undefined && x.count > s.most)
            return `${s.label} counts at most ${s.most}.`;
        terms.push({ label: s.label.toLowerCase().slice(0, STAGED_REQUEST_LIMITS.label), value: bounded(s.target * (x.count ?? 1)) });
    }
    if (req.visibility) {
        const c = rules.visibility?.conditions.find((y) => y.key === req.visibility.condition);
        const vision = rules.visibility?.vision.find((y) => y.key === req.visibility.vision);
        if (!c || !vision)
            return 'That visibility is not one of this game system\'s.';
        const whole = c.target[vision.key] ?? 0;
        const n = kind === 'melee' ? meleeVisibility(rules, c, whole) : whole;
        if (n)
            terms.push({ label: `${c.label.toLowerCase()}, ${vision.label.toLowerCase()}`.slice(0, STAGED_REQUEST_LIMITS.label), value: n });
    }
    // The recoil (issue 1529), after the compensation: the firing's own term.
    if (recoil)
        terms.push({ label: STAGED_RECOIL, value: bounded(recoil) });
    terms.push(...req.modifiers.map((t) => ({ ...t })));
    terms.push(...reach.map((t) => ({ ...t })));
    if (wound)
        terms.push({ label: STAGED_WOUND, value: bounded(wound) });
    return terms;
}
/** One throw made concrete: the test's dice at the TN, thrown; none when there are no dice. */
export function throwOne(d, t, rng) {
    const tn = tnOf(d, t.target, t.terms);
    if (t.dice === 0)
        return { ...t, tn, pool: [] };
    const dice = testDice(d, { dice: t.dice, target: tn });
    if (!dice)
        return 'Those dice cannot be thrown.';
    return { ...t, tn, pool: throwPool(dice, rng) };
}
/** A throw's successes: none on a disaster (every die a 1) or with no dice. */
export function successesOf(rules, t) {
    if (!t.dice)
        return 0;
    const d = parseTestDice(rules);
    const dice = d ? testDice(d, { dice: t.dice, target: t.tn }) : null;
    if (!dice)
        return 0;
    return poolDisaster(dice, t.pool) ? 0 : poolResult(dice, t.pool);
}
const disasterOf = (rules, t) => {
    const d = parseTestDice(rules);
    const dice = d && t.dice ? testDice(d, { dice: t.dice, target: t.tn }) : null;
    return dice ? poolDisaster(dice, t.pool) : false;
};
/** The resistance rating from a token's sheet numbers: the first field of the system's list that the sheet has. */
export function sheetRating(sheet, fields) {
    for (const f of fields) {
        const words = f.which.split('_');
        const n = sheet?.find((x) => x.purpose === f.purpose && words.every((w) => x.words.includes(w)));
        if (n && Number.isInteger(n.value) && n.value >= 1 && n.value <= MAX_POOL)
            return n.value;
    }
    return null;
}
/** The armour rating the attack meets from a token's sheet numbers (`armor_rating` whose words name it), or null. */
export function sheetArmour(sheet, word) {
    if (!word)
        return null;
    const n = sheet?.find((x) => x.purpose === 'armor_rating' && x.words.includes(word));
    return n && Number.isInteger(n.value) && n.value >= 0 && n.value <= 100 ? n.value : null;
}
/** "ballistic" → "Ballistic". */
const capital = (w) => (w ? `${w[0].toUpperCase()}${w.slice(1).replace(/_/g, ' ')}` : w);
/** What a code that adds the wielder's rating is told when the sheet has none (issue 1528). */
export const noWielder = (codes) => `${codes.wielder?.label ?? 'The wielder\'s rating'} could not be read from the sheet: type the Power.`;
/**
 * A weapon's code as thrown (§3.3, issue 1528): the level and kind by the system's lists, and a Strength-based Power
 * worked out from the wielder's Strength. A string is a refusal in words.
 */
export function codeOf(w, codes, kinds, wielder) {
    const level = codes.levels.find((l) => l.key === w.level);
    if (!level)
        return 'That damage level is not one of this game system\'s.';
    const kind = w.damageKind ? kinds.find((k) => k.key === w.damageKind) : kinds[0];
    if (!kind)
        return "That is not one of this game system's kinds of damage.";
    if (w.wielder && (!codes.wielder || wielder === null || wielder === undefined))
        return noWielder(codes);
    const power = w.wielder ? wielder + w.power : w.power;
    if (power < 1 || power > STAGED_REQUEST_LIMITS.power)
        return `A Power of ${power} cannot be thrown.`;
    return { power, level: level.key, kind: kind.key, kindLabel: kind.label, ...(kind.key !== kinds[0]?.key ? { named: true } : {}) };
}
/**
 * The attack made concrete (§5.5 step 1): the TN built, the skill and pool dice thrown. No successes is a miss; else it
 * waits for the defender's dodge (or, with no dodge for a ranged attack, the resistance). A string is a refusal.
 */
/** A melee attack's band in the record (issue 1528): the TN's word, "TN 4 (melee)". */
export const MELEE_BAND = { key: 'melee', label: 'Melee' };
/** What a system without a melee attack is told. */
export const NO_MELEE_RULES = "This table's game system has no melee attack made against a target number.";
export function makeStaged(req, m, rng) {
    const r = m.rules;
    const melee = req.melee ?? null;
    if (melee && !r.melee)
        return NO_MELEE_RULES;
    const band = melee ? null : r.ranges.find((b) => b.key === req.band);
    if (!melee && !band)
        return 'That range is not one of this game system\'s.';
    const fired = codeOf(req.weapon, r.codes, m.kinds, m.wielder);
    if (typeof fired === 'string')
        return fired;
    let code = fired;
    // The firing mode (issue 1529): its recoil joins the TN, and a burst raises the code's Power and level.
    let fire;
    if (req.fire) {
        const mode = r.firing?.modes.find((x) => x.key === req.fire.mode);
        if (!mode)
            return 'That firing mode is not one of this game system\'s.';
        const rounds = fireRounds(mode, req.fire.rounds);
        if (rounds === null)
            return typeof mode.rounds === 'number' ? `${mode.label} fires ${mode.rounds} ${mode.rounds === 1 ? 'round' : 'rounds'}.` : `${mode.label} fires ${mode.rounds.least} to ${mode.rounds.most} rounds.`;
        const at = r.codes.levels.findIndex((l) => l.key === code.level);
        fire = fireOf(mode, rounds, Math.max(0, m.recoil ?? 0), req.fire.compensation, req.fire.heavy && r.firing?.heavy ? r.firing.heavy.times : 1, { power: code.power, level: code.level }, at, r.codes.levels.length - 1);
        const power = code.power + fire.power;
        if (power > STAGED_REQUEST_LIMITS.power)
            return `A Power of ${power} cannot be thrown.`;
        code = { ...code, power, level: r.codes.levels[at + fire.levels].key };
    }
    const reach = reachTerms(melee?.reach);
    const terms = attackTerms(req, r, m.wound, melee ? 'melee' : 'ranged', reach.attacker, fire?.recoil ?? 0);
    if (typeof terms === 'string')
        return terms;
    // The defender's own TN terms in melee, named now (its wound joins at its answer); the visibility's condition is the
    // attacker's, the vision the defender's own.
    let defenderTerms = [];
    if (melee) {
        const vis = req.visibility ? { condition: req.visibility.condition, vision: melee.defender.vision ?? r.visibility?.vision[0]?.key ?? '' } : undefined;
        const t = attackTerms({ situations: melee.defender.situations, ...(vis ? { visibility: vis } : {}), modifiers: melee.defender.modifiers }, r, 0, 'melee', reach.defender);
        if (typeof t === 'string')
            return t;
        defenderTerms = t;
    }
    const target = melee ? r.melee.target : band.target;
    const base = melee ? MELEE_BAND.label.toLowerCase() : band.label.toLowerCase();
    const attack = throwOne(r.dice, { label: req.skill.name, rating: req.skill.rating, spent: m.spent ? { ...m.spent } : null, dice: req.skill.rating + (m.spent?.dice ?? 0), target, base, terms }, rng);
    if (typeof attack === 'string')
        return attack;
    const labelOf = (key) => (key ? { key, label: m.pools.find((p) => p.key === key)?.label ?? capital(key) } : null);
    const dodge = !melee && r.dodge && r.dodge.only !== 'melee' ? r.dodge : null;
    const rules = testDiceData(r.dice);
    const facts = {
        rules, code, codes: parseCodes(codesData(r.codes)),
        band: band ? { key: band.key, label: band.label } : { ...MELEE_BAND }, attack,
        pools: { dodge: dodge ? labelOf(dodge.pool) : null, resist: m.astral ? m.astral.defender : labelOf(r.resist.pool) },
        dodgeTarget: dodge ? dodge.target : null, resistLabel: r.resist.rating[0]?.label ?? capital(r.resist.rating[0]?.which ?? 'rating'),
        ...(melee ? { melee: { ties: r.melee.ties, reach: melee.reach ? { ...melee.reach } : null, target: r.melee.target, terms: defenderTerms, pool: m.astral ? m.astral.defender : m.meleePool ? { ...m.meleePool } : null } } : {}),
        ...(fire ? { fire } : {}),
        ...(m.astral && melee ? { astral: { pool: m.astral.attacker } } : {}),
        ...(m.over ? { over: { ...m.over } } : {}),
    };
    // Melee: the defender always tests, whatever the attack threw (the defender may be the one who hits).
    if (melee)
        return { ...facts, ask: 'melee' };
    if (successesOf(rules, attack) === 0)
        return facts;
    return { ...facts, ask: dodge ? 'dodge' : 'resist' };
}
/**
 * The defender's answer (§5.5 steps 2–3): pool dice for the dodge or the resistance the record waits for, checked
 * against what is left. A dodge with more successes than the attack is a clean miss; else the resistance is asked
 * next, and once thrown the attack is resolved. A string is a refusal in words.
 */
export function answerStaged(f, dice, d, rules, rng, given = {}) {
    if (!f.ask)
        return 'That attack is not waiting for the defender.';
    const kind = stagedArmourKind(f);
    const pool = askedPool(f);
    const left = pool ? d.left[pool.key] ?? null : null;
    if (dice > 0 && (!pool || left === null))
        return 'There is no pool to spend.';
    if (dice > 0 && left !== null && dice > left)
        return `Only ${left} ${pool.label} ${left === 1 ? 'die is' : 'dice are'} left.`;
    const spent = pool && dice > 0 ? { key: pool.key, label: pool.label, dice } : null;
    if (f.ask === 'melee') {
        // The defender's own test (issue 1528; p. 122): the melee skill and pool dice at the melee TN, with its terms and wound.
        const m = f.melee;
        const a = given.melee;
        if (!m)
            return 'That attack is not waiting for the defender.';
        if (!a)
            return 'The defender names the melee skill and the weapon they fight with.';
        const code = codeOf(a.weapon, f.codes, given.kinds ?? [{ key: f.code.kind, label: f.code.kindLabel }], d.wielder);
        if (typeof code === 'string')
            return code;
        const terms = [...m.terms.map((t) => ({ ...t })), ...(d.wound ? [{ label: STAGED_WOUND, value: bounded(d.wound) }] : [])];
        const defence = throwOne(rules.dice, { label: a.skill.name, rating: a.skill.rating, spent, dice: a.skill.rating + dice, target: m.target, base: MELEE_BAND.label.toLowerCase(), terms }, rng);
        if (typeof defence === 'string')
            return defence;
        const next = { ...f, melee: { ...m, defence, weapon: { name: a.weapon.name, ...code } } };
        delete next.ask;
        // Somebody hit: the one hit resists. Nobody (no successes on either side, or a tie that goes to nobody): done.
        return meleeStage(next) ? { ...next, ask: 'resist' } : next;
    }
    if (f.ask === 'dodge') {
        // A burst's rounds make it harder to dodge (issue 1529; p. 113): the mode's term, then the defender's wounds.
        const terms = [...(f.fire?.dodge ? [{ label: f.fire.label.toLowerCase().slice(0, STAGED_REQUEST_LIMITS.label), value: f.fire.dodge }] : []), ...(d.wound ? [{ label: STAGED_WOUND, value: bounded(d.wound) }] : [])];
        const dodge = throwOne(rules.dice, { label: pool?.label ?? 'Dodge', rating: 0, spent, dice, target: f.dodgeTarget ?? 4, base: '', terms }, rng);
        if (typeof dodge === 'string')
            return dodge;
        const next = { ...f, dodge };
        delete next.ask;
        // More dodge successes than the attack's: a clean miss (p. 113). Else the resistance is asked.
        if (successesOf(f.rules, dodge) > successesOf(f.rules, f.attack))
            return next;
        return { ...next, ask: 'resist' };
    }
    if (d.rating === null)
        return `${f.resistLabel} could not be read from the sheet: the DM gives it.`;
    // The armour comes off the Power (p. 113; melee's Impact, p. 123); the wound modifier does not apply (p. 126). In
    // melee the Power is the winner's weapon's, staged (beyond the top level, raised).
    const armourWord = rules.resist.armour[kind];
    const armour = d.armour !== null && d.armour > 0 ? { label: capital(armourWord ?? 'armour'), value: d.armour } : null;
    const terms = armour ? [{ label: armour.label, value: -armour.value }] : [];
    const power = f.melee ? meleeStage(f)?.power ?? f.code.power : f.code.power;
    const resist = throwOne(rules.dice, { label: f.resistLabel, rating: d.rating, spent, dice: d.rating + dice, target: power, base: 'Power', terms }, rng);
    if (typeof resist === 'string')
        return resist;
    const next = { ...f, resist: { ...resist, armour }, ...(f.over ? { over: { ...f.over, rating: d.over ?? d.rating } } : {}) };
    delete next.ask;
    return next;
}
/**
 * The steps that need nobody (§5.5): a defence test whose pool the defender has none of, or none left, is thrown at
 * once with no pool dice — a dodge of nothing, a resistance of the rating alone (Snot, p. 113, had no Combat Pool left
 * after his dodge). A resistance whose rating cannot be read waits for the DM.
 */
export function autoStaged(f, d, rules, rng) {
    let cur = f;
    for (let i = 0; i < 2 && cur.ask; i++) {
        // The defender's melee test always waits: its skill and weapon are the defender's to name.
        if (cur.ask === 'melee')
            return cur;
        const pool = askedPool(cur);
        const left = pool ? d.left[pool.key] ?? 0 : 0;
        if (left > 0)
            return cur;
        if (cur.ask === 'resist' && d.rating === null)
            return cur;
        const next = answerStaged(cur, 0, d, rules, rng);
        if (typeof next === 'string')
            return cur;
        cur = next;
    }
    return cur;
}
/**
 * A melee attack's staging up (issue 1528; p. 123), once both sides have thrown: the winner — more successes; a tie to
 * `ties`; no successes on either side, nobody — its net successes, its weapon's code, the level index staged up to (at
 * most the top) and the Power, raised 1 for each further `stage_per` past the top where `beyond_top.melee` is `power`.
 */
export function meleeStage(f) {
    const m = f.melee;
    if (!m?.defence || !m.weapon)
        return null;
    const a = successesOf(f.rules, f.attack);
    const d = successesOf(f.rules, m.defence);
    if (a === 0 && d === 0)
        return null;
    const winner = a > d ? 'attacker' : d > a ? 'defender' : m.ties === 'attacker' ? 'attacker' : null;
    if (!winner)
        return null;
    const code = winner === 'attacker' ? f.code : m.weapon;
    const levels = f.codes.levels;
    const from = levels.findIndex((l) => l.key === code.level);
    const net = Math.abs(a - d);
    const want = from + Math.floor(net / f.codes.stagePer);
    const top = levels.length - 1;
    const extra = want > top && f.codes.beyondTop.melee === 'power' ? want - top : 0;
    return { winner, net, code, from, want, at: Math.min(top, want), power: Math.min(STAGED_REQUEST_LIMITS.power, code.power + extra), extra };
}
/** Whose test the record waits for (issue 1528): the target's, or — the attacker hit in melee — the attacker's resistance. */
export function stagedAsked(f) {
    return f.ask === 'resist' && f.melee && meleeStage(f)?.winner === 'defender' ? 'attacker' : 'target';
}
/** The side a resolved staged attack's damage lands on. */
export const stagedVictim = (f) => (f.melee && meleeStage(f)?.winner === 'defender' ? 'attacker' : 'target');
/** The pool the step waited for draws on: the dodge's, the resistance's, or the melee test's. */
export function askedPool(f) {
    // Astral combat (issue 1531): the attacker hit by the defender resists from its own state's pool.
    if (f.astral && f.ask === 'resist' && stagedAsked(f) === 'attacker')
        return f.astral.pool;
    return f.ask === 'dodge' ? f.pools.dodge : f.ask === 'melee' ? f.melee?.pool ?? null : f.pools.resist;
}
/** Steps of staging for a net (p. 113): every `stagePer` above 0 up one; every `stagePer` below 0 down one. */
export const stagesFor = (net, stagePer) => (net >= 0 ? Math.floor(net / stagePer) : 0 - Math.floor(-net / stagePer) + 0);
/** Whether the attack's Power is over the over-damage's limit (qa-runs 1169): `times` the rating stamped at the resistance. */
const overApplies = (f, power) => f.over?.rating !== undefined && power > f.over.rating * f.over.times;
/** The outcome as the record stands (§5.5 step 4; melee §5.6). */
export function stagedOutcome(f) {
    if (f.melee)
        return meleeOutcome(f);
    const attack = successesOf(f.rules, f.attack);
    const dodge = f.dodge ? successesOf(f.rules, f.dodge) : null;
    const resist = f.resist ? successesOf(f.rules, f.resist) : null;
    const base = { result: 'waiting', attack, dodge, resist, net: null, level: null, steps: 0 };
    if (attack === 0)
        return { ...base, result: 'missed' };
    if (dodge !== null && dodge > attack)
        return { ...base, result: 'dodged' };
    if (f.ask || resist === null)
        return base;
    const net = attack - (dodge ?? 0) - resist;
    const levels = f.codes.levels;
    const from = levels.findIndex((l) => l.key === f.code.level);
    const want = from + stagesFor(net, f.codes.stagePer);
    // Deadly is the most a ranged attack does (p. 114; `beyond_top: none`); below Light, nothing.
    const at = Math.min(levels.length - 1, want);
    if (at < 0)
        return { ...base, net, steps: want - from, result: 'no_damage' };
    // The optional over-damage (qa-runs 1169): each `stage_per` past the top is one more box.
    const over = want > at && overApplies(f, f.code.power) ? want - at : 0;
    return { ...base, net, steps: at - from, level: { ...levels[at] }, result: 'hit', ...(over ? { over } : {}) };
}
function meleeOutcome(f) {
    const attack = successesOf(f.rules, f.attack);
    const m = f.melee;
    const defence = m.defence ? successesOf(f.rules, m.defence) : null;
    const resist = f.resist ? successesOf(f.rules, f.resist) : null;
    const base = { result: 'waiting', attack, dodge: null, resist, net: null, level: null, steps: 0, defence, winner: null };
    if (defence === null)
        return base;
    const st = meleeStage(f);
    // Nobody hit: no successes on either side, or a tie that goes to nobody.
    if (!st)
        return { ...base, result: 'missed' };
    const won = { ...base, winner: st.winner, net: st.net, power: st.power };
    if (f.ask || resist === null)
        return won;
    // The resistance stages it down from where the winner put it (p. 123): every `stage_per` one step; below the lowest, nothing.
    const pairs = Math.floor(resist / f.codes.stagePer);
    const at = st.at - pairs;
    if (at < 0)
        return { ...won, steps: at - st.from, result: 'no_damage' };
    // The optional over-damage (qa-runs 1169): the pairs past the top are boxes on a hit that stayed at the top. A
    // resistance that staged it down leaves it the plain hit it is without the option.
    const over = pairs === 0 && overApplies(f, st.power) ? Math.max(0, st.want - st.at) : 0;
    return { ...won, steps: at - st.from, level: { ...f.codes.levels[at] }, result: 'hit', ...(over ? { over } : {}) };
}
/** The damage a resolved staged attack deals: the level's boxes, of the code's kind, and the side it lands on. Null for none. */
export function stagedDamage(f) {
    const o = stagedOutcome(f);
    const code = f.melee ? meleeStage(f)?.code ?? f.code : f.code;
    // Over-damage's boxes follow the level's down the kind's chain: a top level fills the first monitor, so they land where
    // the book puts them (p. 126), on the next one along (Stun's on Physical, Physical's on Overflow).
    return o.result === 'hit' && o.level ? { boxes: o.level.boxes + (o.over ?? 0), kind: code.kind, kindLabel: code.kindLabel, level: o.level, victim: stagedVictim(f) } : null;
}
// ── Stored records ────────────────────────────────────────────────────────
export function parseThrow(v, rules, d) {
    if (!isObj(v) || !only(v, ['label', 'rating', 'spent', 'dice', 'bonus', 'target', 'base', 'terms', 'tn', 'pool']))
        return null;
    const bonus = v.bonus === undefined ? undefined : parseDiceTerms(v.bonus);
    if (bonus === null || (bonus && !bonus.length))
        return null;
    if (!isText(v.label, STAGED_REQUEST_LIMITS.name) || !isInt(v.rating, 0, MAX_POOL) || !isInt(v.dice, 0, MAX_POOL) || !isInt(v.target, 1, 200) || typeof v.base !== 'string' || v.base.length > 60 || LINE_BAD.test(v.base))
        return null;
    let spent = null;
    if (v.spent !== null) {
        const s = v.spent;
        if (!isObj(s) || !only(s, ['key', 'label', 'dice']) || !isKey(s.key) || !isText(s.label, 60) || !isInt(s.dice, 1, MAX_POOL))
            return null;
        spent = { key: s.key, label: s.label, dice: s.dice };
    }
    if (v.dice !== Math.max(0, v.rating + (spent?.dice ?? 0) + diceSum(bonus)))
        return null;
    const terms = parseTerms(v.terms, 30);
    if (!terms || v.tn !== tnOf(d, v.target, terms))
        return null;
    let pool;
    if (v.dice === 0) {
        if (!Array.isArray(v.pool) || v.pool.length)
            return null;
        pool = [];
    }
    else {
        const dice = testDice(d, { dice: v.dice, target: v.tn });
        const got = dice ? parsePool(v.pool, dice, 0) : null;
        if (!got)
            return null;
        pool = got;
    }
    void rules;
    return { label: v.label, rating: v.rating, spent, dice: v.dice, ...(bonus ? { bonus } : {}), target: v.target, base: v.base, terms, tn: v.tn, pool };
}
function parseCode(c, codes, extra = []) {
    if (!isObj(c) || !only(c, ['power', 'level', 'kind', 'kindLabel', 'named', ...extra]) || (c.named !== undefined && c.named !== true) || !isInt(c.power, 1, STAGED_REQUEST_LIMITS.power) || !codes.levels.some((l) => l.key === c.level) || !isKey(c.kind) || !isText(c.kindLabel, 24))
        return null;
    return { power: c.power, level: c.level, kind: c.kind, kindLabel: c.kindLabel, ...(c.named ? { named: true } : {}) };
}
const parsePoolLabel = (x) => (x === null ? null : isObj(x) && only(x, ['key', 'label']) && isKey(x.key) && isText(x.label, 60) ? { key: x.key, label: x.label } : undefined);
function parseMeleeFacts(v, rules, d, codes) {
    if (!isObj(v) || !only(v, ['ties', 'reach', 'target', 'terms', 'pool', 'defence', 'weapon']))
        return null;
    if ((v.ties !== 'attacker' && v.ties !== 'none') || !isInt(v.target, 1, 100))
        return null;
    const reach = v.reach === null ? null : parseReach(v.reach);
    const terms = parseTerms(v.terms, 30);
    const pool = parsePoolLabel(v.pool);
    if ((v.reach !== null && !reach) || !terms || pool === undefined)
        return null;
    const defence = v.defence === undefined ? undefined : parseThrow(v.defence, rules, d);
    if (defence === null)
        return null;
    let weapon;
    if (v.weapon !== undefined) {
        const w = v.weapon;
        const c = parseCode(w, codes, ['name']);
        if (!c || !isObj(w) || !isText(w.name, STAGED_REQUEST_LIMITS.name))
            return null;
        weapon = { name: w.name, ...c };
    }
    // The defender's throw and weapon come together, as the answer gives them.
    if (Boolean(defence) !== Boolean(weapon))
        return null;
    return { ties: v.ties, reach, target: v.target, terms, pool, ...(defence ? { defence } : {}), ...(weapon ? { weapon } : {}) };
}
/** Stored staged facts: exact shape or null. Every chain must be what its dice and TN make. */
export function parseStagedFacts(v) {
    if (!isObj(v) || !only(v, ['rules', 'code', 'codes', 'band', 'attack', 'dodge', 'resist', 'ask', 'pools', 'dodgeTarget', 'resistLabel', 'melee', 'fire', 'astral', 'armourAs', 'over']))
        return null;
    if (v.armourAs !== undefined && (v.armourAs !== 'melee' || v.melee !== undefined))
        return null;
    const d = parseTestDice(v.rules);
    if (!d || !d.success || !isObj(v.rules))
        return null;
    const rules = testDiceData(d);
    const codes = isObj(v.codes) ? parseCodes({ levels: v.codes.levels, stage_per: v.codes.stagePer, beyond_top: v.codes.beyondTop, ...(v.codes.wielder !== undefined ? { wielder: v.codes.wielder } : {}) }) : null;
    const code = codes ? parseCode(v.code, codes) : null;
    if (!codes || !code)
        return null;
    const b = v.band;
    if (!isObj(b) || !only(b, ['key', 'label']) || !isKey(b.key) || !isText(b.label, 60))
        return null;
    const attack = parseThrow(v.attack, rules, d);
    if (!attack)
        return null;
    const dodge = v.dodge === undefined ? undefined : parseThrow(v.dodge, rules, d);
    if (dodge === null)
        return null;
    let resist;
    if (v.resist !== undefined) {
        const r = v.resist;
        if (!isObj(r))
            return null;
        const { armour, ...rest } = r;
        const t = parseThrow(rest, rules, d);
        if (!t)
            return null;
        let a = null;
        if (armour !== null) {
            if (!isObj(armour) || !only(armour, ['label', 'value']) || !isText(armour.label, 40) || !isInt(armour.value, 1, 100))
                return null;
            a = { label: armour.label, value: armour.value };
        }
        resist = { ...t, armour: a };
    }
    if (v.ask !== undefined && v.ask !== 'dodge' && v.ask !== 'resist' && v.ask !== 'melee')
        return null;
    const p = v.pools;
    const pd = isObj(p) ? parsePoolLabel(p.dodge) : undefined;
    const pr = isObj(p) ? parsePoolLabel(p.resist) : undefined;
    if (pd === undefined || pr === undefined || !(v.dodgeTarget === null || isInt(v.dodgeTarget, 1, 100)) || !isText(v.resistLabel, 40))
        return null;
    const melee = v.melee === undefined ? undefined : parseMeleeFacts(v.melee, rules, d, codes);
    if (melee === null)
        return null;
    // A melee attack has no dodge, and asks the melee test only of a defender who has not thrown it.
    if (melee && (dodge || (v.ask === 'melee') === Boolean(melee.defence)))
        return null;
    if (!melee && v.ask === 'melee')
        return null;
    const fire = v.fire === undefined ? undefined : parseFire(v.fire, code, codes);
    if (fire === null || (fire && melee))
        return null;
    // Astral combat (issue 1531): a melee attack's, with the attacker's own pool or none.
    let astral;
    if (v.astral !== undefined) {
        const a = v.astral;
        const pool = isObj(a) && only(a, ['pool']) ? parsePoolLabel(a.pool) : undefined;
        if (!melee || pool === undefined)
            return null;
        astral = { pool };
    }
    const over = v.over === undefined ? undefined : parseOver(v.over);
    if (over === null)
        return null;
    return {
        rules, code, codes, band: { key: b.key, label: b.label }, attack,
        ...(dodge ? { dodge } : {}), ...(resist ? { resist } : {}), ...(v.ask ? { ask: v.ask } : {}),
        pools: { dodge: pd, resist: pr }, dodgeTarget: v.dodgeTarget, resistLabel: v.resistLabel,
        ...(melee ? { melee } : {}), ...(fire ? { fire } : {}), ...(astral ? { astral } : {}), ...(v.armourAs === 'melee' ? { armourAs: 'melee' } : {}), ...(over ? { over } : {}),
    };
}
/** A stored over-damage (qa-runs 1169): exact shape. */
function parseOver(v) {
    if (!isObj(v) || !only(v, ['label', 'times', 'rating']) || !isText(v.label, 40) || typeof v.times !== 'number' || !(v.times > 0 && v.times <= 10))
        return null;
    if (v.rating !== undefined && !isInt(v.rating, 0, 1000))
        return null;
    return { label: v.label, times: v.times, ...(v.rating !== undefined ? { rating: v.rating } : {}) };
}
/** A stored firing (issue 1529): exact shape, its recoil and its raise what its numbers make, the code what it raised. */
function parseFire(v, code, codes) {
    const keys = ['mode', 'label', 'rounds', 'prior', 'points', 'free', 'compensation', 'heavy', 'recoil', 'base', 'power', 'levels', 'dodge'];
    if (!isObj(v) || !only(v, keys) || typeof v.mode !== 'string' || !/^[A-Z]{1,4}$/.test(v.mode) || !isText(v.label, 60))
        return null;
    const n = (k, lo, hi) => isInt(v[k], lo, hi);
    if (!n('rounds', 1, FIRING_LIMITS.rounds) || !n('prior', 0, 10_000) || !n('points', 0, 10_000) || !n('free', 0, FIRING_LIMITS.step) || !n('compensation', 0, FIRING_LIMITS.compensation)
        || !n('heavy', 1, 5) || !n('recoil', 0, STAGED_REQUEST_LIMITS.modifier) || !n('power', 0, STAGED_REQUEST_LIMITS.power) || !n('levels', 0, codes.levels.length) || !n('dodge', 0, FIRING_LIMITS.rounds))
        return null;
    const b = v.base;
    if (!isObj(b) || !only(b, ['power', 'level']) || !isInt(b.power, 1, STAGED_REQUEST_LIMITS.power))
        return null;
    const from = codes.levels.findIndex((l) => l.key === b.level);
    const f = v;
    if (from < 0 || f.recoil !== Math.min(STAGED_REQUEST_LIMITS.modifier, Math.max(0, f.prior + f.points - f.free - f.compensation) * f.heavy))
        return null;
    if (code.power !== b.power + f.power || codes.levels[from + f.levels]?.key !== code.level)
        return null;
    return { mode: f.mode, label: f.label, rounds: f.rounds, prior: f.prior, points: f.points, free: f.free, compensation: f.compensation, heavy: f.heavy, recoil: f.recoil, base: { power: b.power, level: b.level }, power: f.power, levels: f.levels, dodge: f.dodge };
}
/** The firing's line (issue 1529): "Full auto, 3 rounds: 8M raised to 11S"; "Semi-automatic" when it raised nothing. */
export function fireLine(f) {
    const x = f.fire;
    if (!x)
        return null;
    const named = f.code.named ? f.code.kindLabel : null;
    const lvl = (k) => f.codes.levels.find((l) => l.key === k);
    const head = `${x.label}${x.rounds > 1 ? `, ${x.rounds} rounds` : ''}`;
    if (!x.power && !x.levels)
        return head;
    return `${head}: ${codeText(x.base.power, lvl(x.base.level), named)} raised to ${codeText(f.code.power, lvl(f.code.level), named)}`;
}
/** The roll's own faces and total: the attack throw's chains, and its successes. */
export const stagedFaces = (f) => poolFaces(f.attack.pool);
// ── The lines (§5.5) ──────────────────────────────────────────────────────
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
const resultText = (rules, t) => (disasterOf(rules, t) ? 'disaster' : plural(successesOf(rules, t)));
/** A code in its letters: "9M", "6S Stun" (the kind named when it is not the system's first). */
export function codeText(power, level, kindLabel) {
    return `${power}${level.abbr}${kindLabel ? ` ${kindLabel}` : ''}`;
}
/** The dice of a throw in words: "Pistols 6 + 4 Combat Pool = 10 dice", "5 Combat Pool", "Body 5". */
const diceWords = (t) => {
    if (t.bonus?.length) {
        const parts = [`${t.label} ${t.rating}`, ...(t.spent ? [`+ ${t.spent.dice} ${t.spent.label}`] : []), ...t.bonus.map((x) => `${x.value < 0 ? '−' : '+'} ${Math.abs(x.value)} ${x.label}`)];
        return `${parts.join(' ')} = ${t.dice} dice`;
    }
    if (!t.rating)
        return t.spent ? `${t.spent.dice} ${t.spent.label}` : `no ${t.label} dice`;
    return t.spent ? `${t.label} ${t.rating} + ${t.spent.dice} ${t.spent.label} = ${t.dice} dice` : `${t.label} ${t.rating}`;
};
/** A throw's TN and its working: "TN 4 (short)", "TN 5 (4 + 1 wounded)", "TN 5 (Power 9 − Ballistic 4)". */
const tnWords = (t) => {
    const asked = t.target + sum(t.terms.map((x) => x.value));
    const raised = asked !== t.tn ? `, at least ${t.tn}` : '';
    const terms = t.terms.map((x) => ` ${signed(x.value)} ${x.label}`).join('');
    // The armour by name and then its rating, as the book writes it: "Power 9 − Ballistic 4".
    if (t.base === 'Power')
        return `TN ${t.tn} (Power ${t.target}${t.terms.map((x) => ` ${x.value < 0 ? '−' : '+'} ${x.label} ${Math.abs(x.value)}`).join('')}${raised})`;
    // A band's name alone when nothing moved it ("TN 4 (short)"); else its number and name first.
    if (t.base)
        return `TN ${t.tn} (${terms || raised ? `${t.target} ${t.base}${terms}${raised}` : t.base})`;
    return `TN ${t.tn}${terms || raised ? ` (${t.target}${terms}${raised})` : ''}`;
};
/** One test's line: "Attack: Pistols 6 + 4 Combat Pool = 10 dice vs TN 4 (short): 3, 4, … → 5 successes". */
export function throwLine(head, rules, t) {
    if (!t.dice)
        return `${head}: ${diceWords(t)} → 0 successes`;
    return `${head}: ${diceWords(t)} vs ${tnWords(t)}: ${poolText(t.pool)} → ${resultText(rules, t)}`;
}
/** A result's boxes: "6 boxes Physical"; with over-damage (qa-runs 1169), "10 boxes Physical + 2 over-damage". */
const boxesWords = (f, lvl, kindLabel, o) => `${lvl.boxes} ${lvl.boxes === 1 ? 'box' : 'boxes'} ${kindLabel}${o.over ? ` + ${o.over} ${f.over.label.toLowerCase()}` : ''}`;
/**
 * The working of the optional over-damage (qa-runs 1169), the DM's alone (it names the one hit's rating): "Over-damage:
 * Power 15 is over Body 6 × 1.5 = 9: 2 more boxes"; or why extra successes past the top added nothing.
 */
export function overLine(f) {
    const o = stagedOutcome(f);
    const v = f.over;
    if (!v || v.rating === undefined || f.ask || o.result !== 'hit')
        return null;
    const st = f.melee ? meleeStage(f) : null;
    const power = st ? st.power : f.code.power;
    const measure = `${f.resistLabel} ${v.rating} × ${v.times} = ${v.rating * v.times}`;
    if (o.over)
        return `${v.label}: Power ${power} is over ${measure}: ${o.over} more ${o.over === 1 ? 'box' : 'boxes'}`;
    const top = f.codes.levels.length - 1;
    const raw = st ? (Math.floor((o.resist ?? 0) / f.codes.stagePer) ? 0 : st.want) : f.codes.levels.findIndex((l) => l.key === f.code.level) + stagesFor(o.net ?? 0, f.codes.stagePer);
    return raw > top && !overApplies(f, power) ? `${v.label}: Power ${power} is not over ${measure}, so the extra successes add nothing` : null;
}
/** The staging line: "Net 5 − 3 = 2: 9M staged up to 9S → 6 boxes Physical". */
export function netLine(f) {
    const o = stagedOutcome(f);
    if (o.net === null)
        return null;
    const levels = f.codes.levels;
    const from = levels.find((l) => l.key === f.code.level);
    const named = f.code.named ? f.code.kindLabel : null;
    const before = codeText(f.code.power, from, named);
    const against = (o.dodge ?? 0) + (o.resist ?? 0);
    const head = `Net ${o.attack} − ${against} = ${o.net < 0 ? `−${-o.net}` : o.net}`;
    if (o.result === 'no_damage')
        return `${head}: ${before} staged down below ${levels[0].label} → no damage`;
    const lvl = o.level;
    const boxes = boxesWords(f, lvl, f.code.kindLabel, o);
    if (!o.steps)
        return `${head}: ${before} → ${boxes}`;
    return `${head}: ${before} staged ${o.steps > 0 ? 'up' : 'down'} to ${codeText(f.code.power, lvl, named)} → ${boxes}`;
}
const sideWord = (w) => (w === 'attacker' ? 'The attacker' : 'The defender');
/**
 * Melee's staging up (issue 1528): "The attacker hits: 7 − 3 = 4: 4M Stun staged up to 4D Stun"; on a tie, the base
 * code; past the top, the Power raised: "… staged up to 5D Stun (+1 Power)".
 */
export function meleeHitLine(f) {
    const st = meleeStage(f);
    const m = f.melee;
    if (!m?.defence)
        return null;
    if (!st)
        return 'No one is hit: neither side scored more successes.';
    const a = successesOf(f.rules, f.attack);
    const d = successesOf(f.rules, m.defence);
    const named = st.code.named ? st.code.kindLabel : null;
    const levels = f.codes.levels;
    const before = codeText(st.code.power, levels[st.from], named);
    const [hi, lo] = st.winner === 'attacker' ? [a, d] : [d, a];
    if (a === d)
        return `${sideWord(st.winner)} hits on the tie, ${a} against ${d}: ${before}`;
    const head = `${sideWord(st.winner)} hits, ${hi} − ${lo} = ${st.net}`;
    if (st.at === st.from && !st.extra)
        return `${head}: ${before}`;
    return `${head}: ${before} staged up to ${codeText(st.power, levels[st.at], named)}${st.extra ? ` (+${st.extra} Power)` : ''}`;
}
/** Melee's staging down by the resistance: "3 resistance successes: 4D Stun staged down to 4S Stun → 6 boxes Stun". */
export function meleeResistLine(f) {
    const o = stagedOutcome(f);
    const st = meleeStage(f);
    if (!st || o.resist === null || f.ask)
        return null;
    const named = st.code.named ? st.code.kindLabel : null;
    const levels = f.codes.levels;
    const before = codeText(st.power, levels[st.at], named);
    const head = `${plural(o.resist).replace('success', 'resistance success')}`;
    if (o.result === 'no_damage')
        return `${head}: ${before} staged down below ${levels[0].label} → no damage`;
    const lvl = o.level;
    const boxes = boxesWords(f, lvl, st.code.kindLabel, o);
    const at = levels.findIndex((l) => l.key === lvl.key);
    if (at === st.at)
        return `${head}: ${before} → ${boxes}`;
    return `${head}: ${before} staged down to ${codeText(st.power, lvl, named)} → ${boxes}`;
}
/** Melee's lines (issue 1528), the DM's whole: both tests, who hits and the staging up, the resistance and the staging down. */
function meleeLines(f) {
    const m = f.melee;
    const lines = [throwLine(f.astral ? 'Astral attack' : 'Attack', f.rules, f.attack)];
    if (m.defence)
        lines.push(throwLine('Defence', f.rules, m.defence));
    const hit = meleeHitLine(f);
    if (hit)
        lines.push(hit);
    if (f.resist)
        lines.push(throwLine('Resist', f.rules, f.resist));
    const down = meleeResistLine(f);
    if (down)
        lines.push(down);
    const over = overLine(f);
    if (over)
        lines.push(over);
    if (f.ask)
        lines.push(`Waiting for the ${f.ask === 'melee' ? 'defence' : 'resistance'}.`);
    return lines;
}
/** Every line of a staged attack (§5.5), the DM's whole: the attack, the dodge, the resistance, the staging, what waits. */
export function stagedLines(f) {
    if (f.melee)
        return meleeLines(f);
    const o = stagedOutcome(f);
    const lines = [throwLine('Attack', f.rules, f.attack)];
    const fired = fireLine(f);
    if (fired)
        lines.push(fired);
    if (o.result === 'missed') {
        lines.push('No successes: a miss.');
        return lines;
    }
    if (f.dodge)
        lines.push(throwLine('Dodge', f.rules, f.dodge));
    if (o.result === 'dodged') {
        lines.push(`Dodged: ${plural(o.dodge)} against ${o.attack}, a clean miss.`);
        return lines;
    }
    if (f.resist)
        lines.push(throwLine('Resist', f.rules, f.resist));
    const net = netLine(f);
    if (net)
        lines.push(net);
    const over = overLine(f);
    if (over)
        lines.push(over);
    if (f.ask)
        lines.push(`Waiting for the ${f.ask === 'dodge' ? 'dodge' : 'resistance'}.`);
    return lines;
}
/** The defender's question (§5.2's `poolAsk`): "Dodge: how many Combat Pool dice?". */
export function stagedQuestion(f) {
    if (!f.ask)
        return null;
    const pool = askedPool(f);
    if (f.ask === 'melee')
        return `Defence: the melee skill, the weapon, and how many ${pool?.label ?? 'pool'} dice?`;
    return `${f.ask === 'dodge' ? 'Dodge' : 'Resistance'}: how many ${pool?.label ?? 'pool'} dice?`;
}
const seesSide = (s, viewer) => s.seen || (s.seenBy?.includes(viewer) ?? false) || (s.ownersSee !== false && s.owners.includes(viewer));
/** Whether the defending token's players answer for it (else the DM): it has owners who can see it. */
export const defenderAnswers = (target) => target.owners.length > 0 && target.ownersSee !== false;
/**
 * A player's part of a staged attack (§5.5): the attacker's own players read the attack's working on their own public
 * attack and the staging; the defending token's own players read the dodge and resistance and the staging, and are
 * asked; everyone else on a public roll reads each test's successes, never its dice or numbers — the defender's Body,
 * armour and wounds, and a monster's skill, stay with the people they belong to.
 */
export function playerStaged(f, roll, sides, viewer) {
    if (f.melee)
        return playerMelee(f, roll, sides, viewer);
    const o = stagedOutcome(f);
    const ownAttack = !roll.private && sides.attacker.owners.includes(roll.actor.id) && sides.attacker.owners.includes(viewer);
    const defending = sides.target.owners.includes(viewer) && sides.target.ownersSee !== false;
    const shown = !roll.private;
    const lines = [];
    if (ownAttack)
        lines.push(throwLine('Attack', f.rules, f.attack));
    else if (shown || defending)
        lines.push(`Attack: ${plural(o.attack)}`);
    // The firing mode (issue 1529) is the attacker's weapon: its own players and the defender read it, as they read the code.
    const fired = ownAttack || defending ? fireLine(f) : null;
    if (fired)
        lines.push(fired);
    if (o.result === 'missed')
        return { lines };
    if (f.dodge)
        lines.push(defending ? throwLine('Dodge', f.rules, f.dodge) : shown ? `Dodge: ${plural(o.dodge)}` : '');
    if (o.result === 'dodged')
        return { lines: lines.filter(Boolean) };
    if (f.resist)
        lines.push(defending ? throwLine('Resist', f.rules, f.resist) : shown ? `Resist: ${plural(o.resist)}` : '');
    const net = ownAttack || defending ? netLine(f) : null;
    if (net)
        lines.push(net);
    const out = { lines: lines.filter(Boolean) };
    if (f.ask) {
        if (defending && seesSide(sides.target, viewer) && defenderAnswers(sides.target)) {
            const pool = f.ask === 'dodge' ? f.pools.dodge : f.pools.resist;
            out.ask = { step: f.ask, token: sides.target.id, question: stagedQuestion(f), pool: pool?.key ?? null };
        }
        else
            out.waiting = defenderAnswers(sides.target) ? 'the defender' : 'the DM';
    }
    return out;
}
/**
 * A player's part of a melee attack (issue 1528), by step 13's rule: each side's own players read their own side's
 * tests in full — the attacker's the attack (on their own public attack), the defender's the defence — and each the
 * resistance of their own token when it is the one hit, and the staging; everyone else on a public roll reads each
 * test's successes. The one asked is the side whose test it waits for: the defender's test, or the resistance of the
 * one hit (the attacker, when the defender wins). A private attack shows only the defender the attack's successes.
 */
function playerMelee(f, roll, sides, viewer) {
    const o = stagedOutcome(f);
    const m = f.melee;
    const ownAttack = !roll.private && sides.attacker.owners.includes(roll.actor.id) && sides.attacker.owners.includes(viewer);
    const attackerSide = !roll.private && sides.attacker.owners.includes(viewer) && sides.attacker.ownersSee !== false;
    const defending = sides.target.owners.includes(viewer) && sides.target.ownersSee !== false;
    const shown = !roll.private;
    const victim = stagedVictim(f);
    const resists = victim === 'attacker' ? attackerSide : defending;
    const lines = [];
    if (ownAttack)
        lines.push(throwLine('Attack', f.rules, f.attack));
    else if (shown || defending)
        lines.push(`Attack: ${plural(o.attack)}`);
    if (m.defence)
        lines.push(defending ? throwLine('Defence', f.rules, m.defence) : shown ? `Defence: ${plural(o.defence ?? 0)}` : '');
    const party = ownAttack || attackerSide || defending;
    const hit = party ? meleeHitLine(f) : null;
    if (hit)
        lines.push(hit);
    else if (m.defence && shown && o.winner)
        lines.push(`${sideWord(o.winner)} hits.`);
    else if (m.defence && shown && o.result === 'missed')
        lines.push('No one is hit.');
    if (f.resist)
        lines.push(resists ? throwLine('Resist', f.rules, f.resist) : shown ? `Resist: ${plural(o.resist)}` : '');
    const down = party ? meleeResistLine(f) : null;
    if (down)
        lines.push(down);
    const out = { lines: lines.filter(Boolean), melee: true };
    if (f.ask) {
        const asked = stagedAsked(f) === 'attacker' ? sides.attacker : sides.target;
        const mine = asked.owners.includes(viewer) && asked.ownersSee !== false && seesSide(asked, viewer) && defenderAnswers(asked) && (asked === sides.target || !roll.private);
        if (mine) {
            const pool = askedPool(f);
            out.ask = { step: f.ask, token: asked.id, question: stagedQuestion(f), pool: pool?.key ?? null };
        }
        else
            out.waiting = defenderAnswers(asked) && (asked === sides.target || !roll.private) ? (asked === sides.target ? 'the defender' : 'the attacker') : 'the DM';
    }
    return out;
}
export function parsePlayerStaged(v) {
    if (!isObj(v) || !Array.isArray(v.lines) || !v.lines.every((l) => typeof l === 'string'))
        return null;
    return v;
}
// ── The form's helpers (the browser's preview; the server decides) ─────────
/**
 * A damage code as a sheet writes it (§3.4 `damage_code`): "9M", "6L Stun" — the Power, the level's letters, and the
 * kind of damage by its label when it is not the system's first. Null when it is not one of the system's.
 */
export function parseCodeText(text, codes, kinds) {
    const m = /^\s*(\d{1,3})\s*([A-Z]{1,3})(?:\s+([A-Za-z]{1,24}))?\s*$/.exec(text);
    if (!m)
        return null;
    const power = Number(m[1]);
    const level = codes.levels.find((l) => l.abbr === m[2]);
    if (!level || power < 1 || power > STAGED_REQUEST_LIMITS.power)
        return null;
    if (!m[3])
        return { power, level: level.key };
    const kind = kinds.find((k) => k.label.toLowerCase() === m[3].toLowerCase() || k.key === m[3].toLowerCase());
    return kind ? { power, level: level.key, ...(kind.key !== kinds[0]?.key ? { damageKind: kind.key } : {}) } : null;
}
/**
 * The band a distance falls in (§3.3): the first whose top (`bands`, the weapon's, in the system's unit) it does not
 * pass, by the system's bands in order; past the last, null (out of range: the attacker may still choose one).
 */
export function bandAt(distance, bands, ranges) {
    if (!bands || bands.length !== ranges.length)
        return null;
    const d = Math.round(distance * 10) / 10;
    const i = bands.findIndex((top) => d <= top);
    return i < 0 ? null : ranges[i].key;
}
/** The straight distance between two tokens in the system's unit: their centres, squares by the grid's pitch. */
export function tokenDistance(a, b, grid, perSquare) {
    return Math.hypot((b.x - a.x) / grid.x.basePitch, (b.y - a.y) / grid.y.basePitch) * perSquare;
}
//# sourceMappingURL=staged.js.map